
import React, { FC, useState } from 'react'
import storage from 'src/components/Core/Utils/storage'
import t from '../../../../../translations/main'
import globalConfig from 'src/components/Organisms/Form/config'
import { getBaseApiHost, getEndpointUrl } from 'src/components/Core/Utils/url_constants'
import { checkMode, isInvalidValue } from 'src/components/Organisms/Form/utils'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'

import Button from '../../../../../components/Atoms/Button/Button'
import Font from 'src/components/Core/Typography/Font/Font'
import Flex from 'src/components/Layout/Flex/Flex'
import Input from 'src/components/Atoms/Input/Input'
import Icon from 'src/components/Core/Iconography/Icon/Icon'
import Table from 'src/components/Organisms/Table/Table'
import {
  FormStyle,
  FormHeader,
  FormTitle,
  FormFooterActionsRight,
  FormFooterActionsLeft,
  FormFooter,
} from '../../../../../components/Organisms/Form/Form.styles'
import { ApiData, FormProps } from '../../../../Pages/Onboarding/types'
import { AuthSigsProps, FormSignatoryFields } from './types'
import { useParams } from 'react-router-dom'
import { stringToNumber } from 'src/views/Pages/Onboarding/Onboarding'

const signatoriesColumns = [
  {
    Header: t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.addSignatories.table.col1'),
    accessor: 'col1',
  },
  {
    Header: t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.addSignatories.table.col2'),
    accessor: 'col2',
  },
  {
    Header: '',
    accessor: 'col3',
  },
]

const AuthorizedSignatoriesForm: FC<AuthSigsProps & FormProps> = props => {
  const { fetchedData, formType, setSubmittedSuccesifully } = props
  const [formData, setFormData] = useState<FormSignatoryFields>({
    firstName: {
      value: '',
      error: ''
    },
    lastName: {
      value: '',
      error: ''
    },
    email: {
      value: '',
      error: ''
    }
  })
  // @ts-ignore:next-line
  const [signatories, setSignatories] = useState<ApiData[]>(Object.values(fetchedData))
  const { sendRequest } = useFetch()

  const { step } = useParams()

  const applicationId = storage.get(storage?.keys?.onboarding?.applicationId)

  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    sendRequest(
      {
        scope: 'onboarding',
        url: (import.meta.env.VITE_MOCK_DATA || 'false') === 'true'
          ? `${window.location.origin}/mock/postMessage.json`
          : `${getBaseApiHost()}/${formType === 'Corporate' ? 'CorpAOF/UpdateSignatories24' : 'DeliverableAOF/UpdateSignatories18'}?applicationId=${applicationId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
      data => {
        if(data) {
          if(props.isLastForm){
            const modifyResponseForSubmit = (data?: ApiData) => {
              if(!data.reasonPhrase){
                storage.set(storage?.keys?.onboarding?.applicationStatus,'submitted')
                setSubmittedSuccesifully && setSubmittedSuccesifully(true)
                props.nextStep(true)
              }
            }
            const applicationType = formType === 'Corporate' ? 'CorpAOF' : 'DeliverableAOF'
            const currentStep = stringToNumber(step) - 1
            sendRequest(
              {
                scope: 'onboarding',
                url: import.meta.env.VITE_MOCK_DATA === 'true'
                  ? `${window.location.origin}/mock/postMessage.json`
                  : `${getBaseApiHost()}/${applicationType}/submit${applicationType}Application?applicationid=${applicationId}&step=${currentStep}`,
                method: 'POST',
                body: { },
              },
              modifyResponseForSubmit
            )
          } else  {
            props.nextStep()
          }
        }
      }
    )
  }

  // @ts-ignore:next-line
  const hasValidationError = () => {
    let errors = Object.entries(formData).reduce((acc, [field, value]) => {
      if(!value.value) {
      // @ts-ignore:next-line
        setFormData(prev => ({...prev, [`${field}`]: {...prev[`${field}`], error: t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.addSignatories.errors.emptyField')}}))
        return ++acc
      }
      return acc
    }, 0)
    if(isInvalidValue(formData.email.value, globalConfig?.regex?.email)) {
      setFormData(prev => ({...prev, email: {...prev.email, error: t('error.invalidValue')}}))
      ++errors
    }
    return errors
  }

  const addSignatory = () => {
    sendRequest(
      {
        scope: 'onboarding',
        url: getEndpointUrl(`${formType === 'Corporate' ? 'CorpAOF/addSignatory24' : 'DeliverableAOF/addSignatory18'}`, undefined, 'signatory-add.json'),
        method: 'POST',
        body: {
          applicationId,
          signatoryFirstName: formData.firstName.value,
          signatoryLastName: formData.lastName.value,
          signatoryEmailAddress: formData.email.value,
        }
      },
      data => {
        if(!data.reasonPhrase) {
          // @ts-ignore:next-line
          setSignatories(prev => {
            return [...prev, {signatoryFirstName: formData.firstName.value, signatoryLastName: formData.lastName.value, signatoryEmailAddress: formData.email.value, signatoryID: data}]
          })
          setFormData({firstName: {value: '', error: ''} , lastName: {value: '', error: ''}, email: {value: '', error: ''}})
        }
      }
    )
  }

  const deleteSignatory = (id: number) => {
    if(!id && id !== 0) return
    sendRequest(
      {
        scope: 'onboarding',
        url: getEndpointUrl(`${formType === 'Corporate' ? 'CorpAOF/removeSignatory24' : 'DeliverableAOF/removeSignatory18'}`, applicationId, 'signatory-delete.json', `SignatureId=${id}`),
        method: 'POST',
      },
      data => {
        if(data) {
          // @ts-ignore:next-line
          setSignatories(prev => {
            const temp = [...prev]
            // @ts-ignore:next-line
            const deletedItemIndex = prev.findIndex(item => item.signatoryID === id)
            temp.splice(deletedItemIndex, 1)
            return temp
          })
          setFormData({firstName: {value: '', error: ''} , lastName: {value: '', error: ''}, email: {value: '', error: ''}})
        }
      }
    )
  }

  // @ts-ignore:next-line
  const overviewTableData = signatories.map(obj => ({
    col1: <Font bold oneLine>{`${obj.signatoryFirstName} ${obj.signatoryLastName}`}</Font>,
    col2: <Font bold oneLine>{obj.signatoryEmailAddress}</Font>,
    col3: <Icon name='close' size='s' color='error' clickFunction={() => deleteSignatory(obj.signatoryID)} />,
  }))

  return (
    <Flex maxWidth flexDirection='column' alignItems='flex-start'>
      <FormHeader>
        <FormTitle>
          {t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.title')}
        </FormTitle>
      </FormHeader>
      <Font>{t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.addSignatories.descriptionText')}</Font>
      <Font margin='28px 0 12px 0' tag='h5' bold>{t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.addSignatories.addSignatories')}</Font>
      <Flex gap='16px' maxWidth>
        <Input
          label={t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.inputs.firstName.label')}
          placeholder={t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.inputs.firstName.placeholder')}
          onChange={event => {
            const result = event.target.value.replace(/[^a-z ]/gi, '')
            setFormData(prev => ({...prev, firstName: {value: result, error: ''}}))
          }
          }
          value={formData.firstName.value}
          required
          maxlength='100'
          error={formData.firstName.error}
        />
        <Input
          label={t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.inputs.lastName.label')}
          placeholder={t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.inputs.lastName.placeholder')}
          onChange={event => {
            const result = event.target.value.replace(/[^a-z ]/gi, '')
            setFormData(prev => ({...prev, lastName: {value: result, error: ''}}))
          }
          }
          value={formData.lastName.value}
          required
          maxlength='100'
          error={formData.lastName.error}
        />
        <Input
          label={t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.inputs.email.label')}
          placeholder={t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.inputs.email.placeholder')}
          onChange={e => setFormData(prev => ({...prev, email: {value: e.target.value, error: ''}}))}
          type='email'
          value={formData.email.value}
          required
          maxlength='255'
          error={formData.email.error}
        />
        <Button
          onClick={() => {
            if(hasValidationError()) return
            addSignatory()
          }}
          label={t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.addSignatories.add')}
          overflow='visible'
          version={2}
          disabled={signatories?.length >= 3}
        />
      </Flex>
      <Flex maxWidth>
        {
          signatories?.length === 0
            ? <Font tag='h2' bold>{t('onboarding.forms.DeliverableAccount.AuthorizedSignatoriesForm.messages.noSignatories')}</Font>
            : <Table
              tableData={overviewTableData}
              tableColumns={signatoriesColumns}
              tableReadOnly={checkMode('readOnly')}
              fixedColumnsWidth
            />
        }
      </Flex>
      {/* </Accordion> */}
      <FormStyle onSubmit={e => submitForm(e)}>
        <FormFooter noOfButtons={2}>
          <FormFooterActionsLeft>
            <Button
              label={t('onboarding.main.backButton')}
              version={2}
              disabled={props.disablePrev}
              onClick={props.prevStep}
            />
          </FormFooterActionsLeft>
          <FormFooterActionsRight>
            <Button
              type='submit'
              label={props.isLastForm ? t('onboarding.main.saveAndSubmit') : t('onboarding.main.continueButton')}
              disabled={ signatories?.length === 0 || signatories?.length > 3 }
            />
          </FormFooterActionsRight>
        </FormFooter>
      </FormStyle>
    </Flex>
  )
}

export default AuthorizedSignatoriesForm
